import Vue from "vue";
import mezr from "mezr";
import uniqid from "uniqid";
import { DateTime } from "luxon";
import { Validator } from "vee-validate";
import {
    assign,
    clone,
    cloneDeep,
    debounce,
    difference,
    differenceWith,
    flatten,
    filter,
    find,
    findIndex,
    get,
    includes,
    isArray,
    isNaN,
    isObject,
    isEmpty,
    isString,
    isUndefined,
    keys,
    map,
    merge,
    padStart,
    size,
    some,
    sortBy,
    throttle,
    union,
    omit,
    isNumber
} from "lodash"; // Lodash tree shaking

const entities = require("entities");
function throwErrorParams() {
    throw new Error("Missing parameter");
}

// -- Global mixins. Use global mixins sparsely and carefully, because
// -- it affects every single Vue instance created, including third party components.
Vue.mixin({
    data() {
        return {
            SURVEYJS_VERSION: "1.9.130", // Also need to change in src/module/schedule/previsit/webform.vue (Manually change each time upgrade version)
            SYSTEM_COMPANY_NAME: "DaySmart Vet", // Previously Vetter Software Inc.
            get EMAIL_TYPE() {
                return {
                    APPOINTMENT_CONFIRMATION: 1,
                    APPOINTMENT_REMINDER: 2,
                    BIRTHDAY: 3,
                    BOARDING_CONFIRMATION: 4,
                    BOARDING_REMINDER: 5,
                    CERTIFICATE: 6,
                    CHECKOUT_DOCUMENT: 7,
                    DEPOSIT: 8,
                    ESTIMATE: 9,
                    INVOICE: 10,
                    LETTER: 11,
                    MEDICAL_NOTE: 12,
                    MEDICAL_REMINDER: 13,
                    PATIENT_HISTORY: 14,
                    RETURN: 15,
                    STATEMENT: 16,
                    APPOINTMENT_ACCEPT: 17,
                    APPOINTMENT_RESCHEDULE: 18,
                    APPOINTMENT_DECLINE: 19,
                    LAB: 20,
                    RECEIPT: 21
                };
            },
            get ACTOR_REFERRAL() {
                return {
                    SOURCE_CONTACT: 0,
                    SOURCE_COMPANY: 1,
                    SOURCE_CAMPAIGN: 2
                };
            },
            get REMINDER_TYPE() {
                return {
                    EMAIL: 1, // requires email
                    PHONE: 2, // requires phone (at least one of home, work or mobile)
                    MAIL: 3, // requires address (at least address, city, zipcode, state)
                    NO_REMINDER: 4, //
                    SMS: 5 // requires mobile
                };
            },
            get STATUS_ACTION() {
                return {
                    WRITEOFF: 1,
                    REFUND: 2
                };
            },
            get STATUS_ACTION_OPTION() {
                return {
                    DO_NOTHING: 0,
                    WRITEOFF_BALANCE: 1,
                    REFUND_BALANCE: 2
                };
            },
            get BILLING_TYPE() {
                return {
                    CREDITS: 3,
                    ESTIMATES: 1,
                    ALL_INVOICES: 2,
                    LOCKED_INVOICES: 10,
                    OPEN_INVOICES: 9,
                    REOPENED_INVOICES: 13,
                    PAID_INVOICES: 11,
                    LATE_FEES: 8,
                    PAYMENTS: 4,
                    REFUNDS: 6,
                    RETURNS: 5,
                    WRITE_OFFS: 7
                };
            },
            task: {
                TIME_10_MINUTES: 10 * 60000,
                TIME_10_SECONDS: 10000,
                my_interval: null,
                bell_interval: null,
                bell_top_interval: null
            },
            get SEX() {
                return {
                    1: "Male (intact)",
                    2: "Male (neutered)",
                    3: "Female (intact)",
                    4: "Female (spayed)",
                    5: "Unknown"
                };
            },
            get SYS_DATEFORMAT() {
                return {
                    MMDDYYYY: "MM/DD/YYYY",
                    DDMMYYYY: "DD/MM/YYYY",
                    YYYYMMDD: "YYYY/MM/DD",
                    MDDYYYY: "M DD, YYYY",
                    DDMYYYY: "DD M YYYY",
                    DD_M_YYYY: "DD-M-YYYY"
                };
            },
            get vitalweight_resource() {
                return { 72: "Kg", 73: "G", 75: "Lbs", 76: "Oz" };
            },
            get weight_resource() {
                return { 90: "mg", 91: "kg", 92: "g", 93: "lbs", 94: "oz" };
            },
            get MODULE() {
                return {
                    CLIENT: "ACTOR",
                    PATIENT: "PATIENT"
                };
            },
            PER_PAGE: 20,
            USE_PATIENT_MODULE_VUE:
                process.env.USE_PATIENT_MODULE_VUE === "true",
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true",
            USE_INVENTORY_MODULE_VUE:
                process.env.USE_INVENTORY_MODULE_VUE === "true",
            USE_BILLING_MODULE_VUE:
                process.env.USE_BILLING_MODULE_VUE === "true",
            USE_INVOICE_MODULE_VUE:
                process.env.USE_INVOICE_MODULE_VUE === "true",
            USE_ESTIMATE_MODULE_VUE:
                process.env.USE_ESTIMATE_MODULE_VUE === "true",
            USE_NEW_THEME: process.env.USE_NEW_THEME === "true",
            get COST_TYPE() {
                return [
                    { value: 1, text: "Fixed" },
                    { value: 2, text: "% of latest cost (excluding $0)" },
                    { value: 3, text: "% of average cost (excluding $0)" },
                    { value: 4, text: "% of highest cost" },
                    { value: 5, text: "% of lowest cost (excluding $0)" }
                ];
            },
            get ATTACHMENT_OPTION() {
                return {
                    ATTACHMENT_UPLOAD_NEW: 0,
                    ATTACHMENT_LIBRARY: 1,
                    ATTACHMENT_LIFELEARN: 2
                };
            },
            get ATTACHMENT_SOURCE_TYPE() {
                return {
                    SOURCE_TYPE_CLIENT: 1,
                    SOURCE_TYPE_PERSON: 2,
                    SOURCE_TYPE_COMPANY: 3,
                    SOURCE_TYPE_LAB: 4,
                    SOURCE_TYPE_LAB_EXTERNAL: 5,
                    SOURCE_TYPE_LIFE_LEARN: 6
                };
            },
            TIMEOUT_REF: {
                // id : minutes
                1: 15,
                2: 30,
                3: 60,
                4: 120,
                5: 240,
                6: 480,
                7: 720
            }
        };
    },
    mounted() {
        // Due to limitation of certain browser e.g: firefox, below code is a workaroud for
        // numeric value using type=text and v-validate custom rules
        // reference: https://vee-validate.logaretm.com/v2/guide/custom-rules.html#using-the-custom-rule
        let instance = new Validator({ trueField: "truthy" });
        instance.extend("numericalWithDecimal", value => {
            return !isNaN(Number(value));
        });
        instance.attach({
            name: "falseField",
            rules: "numericalWithDecimal"
        });
    },
    computed: {
        env() {
            return {
                APPS: process.env.APPS,
                VUE_APP_ENV: process.env.VUE_APP_ENV
            };
        },
        _setting() {
            return this.$store.state.setting;
        },
        _timeZone() {
            return get(this, "$root.currentActor.timezone") &&
                this.$root.currentActor.timezone !== "0"
                ? this.$root.currentActor.timezone
                : get(this, "$root.setting.timezone")
                ? this.$root.setting.timezone
                : "America/Los_Angeles";
        },
        _dateFormat() {
            return this._getLuxonFormat();
        },
        _isTvView() {
            return this.$route.path.includes("display/");
        },
        _isEnhancedLayout() {
            return this.$store.state.isEnhancedLayout;
        },
        _isDesktopView() {
            const minWidth = 1400;

            if (window.matchMedia(`(min-width: ${minWidth}px)`).matches) {
                return true;
            }
            return false;
        },
        _() {
            return {
                assign,
                clone,
                cloneDeep,
                debounce,
                difference,
                differenceWith,
                flatten,
                filter,
                find,
                findIndex,
                get,
                includes,
                isArray,
                isNaN,
                isObject,
                isEmpty,
                isString,
                isUndefined,
                keys,
                map,
                merge,
                padStart,
                size,
                some,
                sortBy,
                throttle,
                union,
                omit
            };
        }
    },
    methods: {
        _hasAccess(permission) {
            let access = this.$root.permissions[permission];

            if (
                this.$root.currentActor.issuper == true ||
                this.$root.permissions.issuper
            ) {
                return true;
            }

            return access;
        },
        /**
         * @method _redirectDefaultPage
         * @returns REDIRECT TO THE AUTHORIZE PAGE
         * @see apps/application/controllers/welcome.php Line:318
         * @see apps/application/models/orm/TblActorPeer.php Line:267
         */
        _redirectDefaultPage() {
            let redirectedPage = "";
            if (this._hasAccess("schedule.access")) {
                redirectedPage = "schedule";
            } else if (this._hasAccess("patient.access")) {
                redirectedPage = "patient";
            } else if (this._hasAccess("client.access")) {
                redirectedPage = "client";
            } else if (this._hasAccess("contact.access")) {
                redirectedPage = "contact";
            } else if (this._hasAccess("inventory.access")) {
                redirectedPage = "inventory";
            } else if (this._hasAccess("billing.access")) {
                redirectedPage = "billing";
            } else if (this._hasAccess("report.access")) {
                redirectedPage = "report";
            } else if (this._hasAccess("setting.access")) {
                window.location = process.env.APPS + "business/business";
                return;
            } else {
                if (typeof newrelic !== "undefined") {
                    // eslint-disable-next-line no-undef
                    newrelic.addPageAction("view-logout", {
                        reason: "redirect",
                        file: "commons/helper.js"
                    });
                }
                window.location =
                    process.env.APPS + "index.php/welcome/logout?f=3";
                return;
            }
            this.$router.push({
                name: redirectedPage
            });
        },
        addScript(src) {
            let doc = document.createElement("script");
            doc.setAttribute("src", src + "?ts=" + new Date().getTime());
            document.head.appendChild(doc);
        },
        addScriptPromise(src) {
            return new Promise((resolve, reject) => {
                let doc = document.createElement("script");
                doc.setAttribute("src", src + "?ts=" + new Date().getTime());
                doc.addEventListener("load", () => {
                    // Script has loaded successfully
                    resolve();
                });
                doc.addEventListener("error", () => {
                    // Script failed to load
                    console.error(`Failed to load script: ${src}`);
                    reject(new Error(`Failed to load script: ${src}`));
                });
                document.head.appendChild(doc);
            });
        },
        addCSS(src) {
            let doc = document.createElement("link");
            doc.setAttribute("href", src + "?ts=" + new Date().getTime());
            doc.setAttribute("rel", "stylesheet");
            document.head.appendChild(doc);
        },
        _message(
            text,
            variant,
            duration,
            width,
            params,
            title,
            isLegacy = false // if true, use the old alert style
        ) {
            if (!text || text === "") {
                return;
            }

            if (!variant) {
                variant = "success";
            }

            if (!isLegacy) {
                if (
                    duration === -1 ||
                    duration === "-1" ||
                    duration === true ||
                    variant === "danger"
                ) {
                    duration = 0;
                } else if (duration === null || duration === undefined) {
                    duration = 4000;
                }

                /*
                the banner for "task" requires special handling.
                it should not appear too many times. so for that, we first
                detect if a task banner is currently being displayed,
                then there's no need to repeat it.
                */
                if (params?.isTask) {
                    if (window.taskBanner) {
                        return;
                    }

                    window.taskBanner = window.toast.open({
                        type: variant,
                        message: text,
                        duration: duration
                    });

                    setTimeout(() => {
                        window.taskBanner = null;
                    }, duration);

                    window.taskBanner.on("dismiss", () => {
                        if (params && params["close-callback"]) {
                            params["close-callback"]();
                        }
                    });
                } else if (params?.isPaymentProcessing) {
                    window.paymentProcessingBanner = window.toast.open({
                        type: variant,
                        message: text,
                        duration: duration
                    });
                } else if (variant === "danger") {
                    if (window.dangerBanner) {
                        window.toast.dismiss(window.dangerBanner);
                    }

                    window.dangerBanner = window.toast.open({
                        type: variant,
                        message: text,
                        duration: duration
                    });

                    window.dangerBanner.on("dismiss", () => {
                        window.dangerBanner = null;
                    });
                } else {
                    window.toast.open({
                        type: variant,
                        message: text,
                        duration: duration
                    });
                }

                return;
            }

            this.$root.$emit(
                "message",
                text,
                variant,
                duration,
                width,
                params,
                title
            );
        },
        // Alias for getDateFormat
        _getDateFormat(format) {
            return this.getDateFormat(format);
        },
        getDateFormat(format) {
            let oFmt = format ? format : this.$root.setting.date_format;

            switch (oFmt) {
                case this.SYS_DATEFORMAT.MMDDYYYY:
                    return "MM/DD/YYYY";
                case this.SYS_DATEFORMAT.DDMMYYYY:
                    return "DD/MM/YYYY";
                case this.SYS_DATEFORMAT.YYYYMMDD:
                    return "YYYY/MM/DD";
                case this.SYS_DATEFORMAT.MDDYYYY:
                    return "MMM DD, YYYY";
                case this.SYS_DATEFORMAT.DDMYYYY:
                    return "DD MMM YYYY";
                case this.SYS_DATEFORMAT.DD_M_YYYY:
                    return "DD-MMM-YYYY";
                default:
                    return "MMM DD, YYYY";
            }
        },
        _getLuxonFormat(format) {
            let oFmt = format ? format : this.$root.setting.date_format;

            switch (oFmt) {
                case this.SYS_DATEFORMAT.MMDDYYYY:
                    return "MM/dd/yyyy";
                case this.SYS_DATEFORMAT.DDMMYYYY:
                    return "dd/MM/yyyy";
                case this.SYS_DATEFORMAT.YYYYMMDD:
                    return "yyyy/LL/dd";
                case this.SYS_DATEFORMAT.MDDYYYY:
                    return "MMM dd, yyyy";
                case this.SYS_DATEFORMAT.DDMYYYY:
                    return "dd MMM yyyy";
                case this.SYS_DATEFORMAT.DD_M_YYYY:
                    return "dd-MMM-yyyy";
                default:
                    return "MMM dd, yyyy";
            }
        },
        /* Contruct string to send CI
         */
        _bindReferrer(referrer) {
            if (referrer) {
                if (!/\[(.*?)\]/gi.test(referrer.id)) {
                    switch (referrer.type) {
                        case this.ACTOR_REFERRAL.SOURCE_CONTACT:
                            return "[actor]" + referrer.id;
                        case this.ACTOR_REFERRAL.SOURCE_COMPANY:
                            return "[business]" + referrer.id;
                        case this.ACTOR_REFERRAL.SOURCE_CAMPAIGN:
                            return "[campaign]" + referrer.id;
                        default:
                            return "";
                    }
                } else {
                    return referrer.id;
                }
            }
        },
        // -- source from https://awik.io/determine-color-bright-dark-using-javascript/
        _lightOrDark(color) {
            // Variables for red, green, blue values
            if (!color) {
                color = "yellow";
            }

            var r, g, b, hsp;

            // Check the format of the color, HEX or RGB?
            if (color.match(/^#(?:[0-9a-fA-F]{3}){1,2}$/)) {
                color = +(
                    "0x" +
                    color.slice(1).replace(color.length < 5 && /./g, "$&$&")
                );

                r = color >> 16;
                g = (color >> 8) & 255;
                b = color & 255;
            } else {
                // If RGB --> Convert it to HEX: http://gist.github.com/983661
                if (!color.match(/^rgb/)) {
                    let d = document.createElement("div");
                    d.style.color = color;
                    document.body.appendChild(d);
                    //Color in RGB
                    color = window.getComputedStyle(d).color;
                    document.body.removeChild(d);
                }

                // If HEX --> store the red, green, blue values in separate variables
                color = color.match(
                    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
                );

                r = color[1];
                g = color[2];
                b = color[3];
            }

            // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
            hsp = Math.sqrt(
                0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b)
            );

            // Using the HSP value, determine whether the color is light or dark
            if (hsp > 150 /* 127.5 */) {
                return "light";
            } else {
                return "dark";
            }
        },
        _stripTags(string) {
            return string.replace(/(<([^>]+)>)/gi, "");
        },
        _splitMulti(str, seperator) {
            var tempChar = seperator[0];
            for (var i = 1; i < seperator.length; i++) {
                str = str.split(seperator[i]).join(tempChar);
            }
            str = str.split(tempChar);
            return str;
        },
        _sanitizeFilename(string, force_lowercase = true) {
            let self = this;
            let strip = [
                "~",
                "`",
                "!",
                "@",
                "#",
                "$",
                "%",
                "^",
                "&",
                "*",
                "(",
                ")",
                "=",
                "+",
                "[",
                "{",
                "]",
                "}",
                "\\",
                "|",
                ";",
                ":",
                '"',
                "'",
                "&#8216;",
                "&#8217;",
                "&#8220;",
                "&#8221;",
                "&#8211;",
                "&#8212;",
                "â€”",
                "â€“",
                ",",
                "<",
                ".",
                ">",
                "/",
                "?",
                "-",
                "#"
            ];
            string = string.split(" ").join("_");
            let clean = self
                ._splitMulti(self._stripTags(string), strip)
                .join("_")
                .trim();
            clean = clean.replace(/\s+/g, "");

            return force_lowercase ? clean.toLowerCase() : clean;
        },
        _get_filename(str) {
            let last_index_of = str.lastIndexOf(".");
            let name = str;

            if (last_index_of !== 0) {
                name = str.substr(0, last_index_of);
            }
            return name;
        },
        _get_extension(str) {
            let last_index_of = str.lastIndexOf(".");
            let ext = "";

            if (last_index_of !== 0) {
                ext = str.substr(last_index_of + 1);
            }

            return ext;
        },
        _get_fileparts(str) {
            let self = this;
            let fname = self._get_filename(str);
            let fext = self._get_extension(str);

            return [fname, fext];
        },
        _s3URL(module, id) {
            return (
                process.env.S3_URI +
                module +
                "/" +
                id
                    .replace(/[-]/g, "_")
                    .match(/.{1,2}/g)
                    .join("/") +
                "/"
            );
        },
        _s3Path(module, id) {
            return (
                module +
                "/" +
                id
                    .replace(/[-]/g, "_")
                    .match(/.{1,2}/g)
                    .join("/") +
                "/"
            );
        },
        _datePickerShow(
            event,
            isFixedPosition = false,
            isUpdatePosition = true
        ) {
            let self = this;
            let position = isUpdatePosition
                ? {
                      top:
                          mezr.offset([event.target, "border"], window).top +
                          mezr.height(event.target, "border") +
                          "px",
                      left: mezr.offset(event.target).left + "px"
                  }
                : {};

            if (isFixedPosition) {
                position.position = "fixed";
            }

            self.$nextTick(() => {
                $(".bootstrap-datetimepicker-widget").css(position);
            });

            $(".modal-body")
                .off("scroll")
                .on(
                    "scroll",
                    throttle(function () {
                        self._datePickerShow(event);
                        $(event.target).on("dp.hide", function () {
                            $(".modal-body").off("scroll");
                        });
                    }, 300)
                );
        },
        /**
         * Multi format to date time converter
         * @param value - Multi object
         * @returns a valid DateTime object
         *  */
        _toDateTime(value, convertTz = true) {
            let time = DateTime.local();
            if (!value) {
                return null;
            }
            if (moment.isMoment(value)) {
                time = new DateTime.fromISO(value.toISOString(true));
            } else if (value instanceof Date) {
                time = new DateTime.fromJSDate(value);
            } else if (value instanceof DateTime) {
                time = value;
            } else if (isString(value)) {
                time = new DateTime.fromISO(value, {
                    zone: convertTz ? this._timeZone : "UTC"
                });

                if (time.isValid) {
                    return time;
                }
                time = new DateTime.fromFormat(
                    value,
                    "yyyy-MM-dd HH:mm:ss.SSS000 ZZ",
                    { zone: convertTz ? this._timeZone : "UTC" }
                );
                if (time.isValid) {
                    return time;
                }
                time = new DateTime.fromFormat(
                    value,
                    "yyyy-MM-dd HH:mm:ss.SSS000",
                    { zone: convertTz ? this._timeZone : "UTC" }
                );
                if (time.isValid) {
                    return time;
                }

                if (!time.isValid) {
                    return new DateTime.fromFormat(value, "h:m a");
                }
            } else if (isNumber(value)) {
                time = new DateTime.fromSeconds(value);
            } else {
                time = value;
            }
            return time.setZone(convertTz ? this._timeZone : "UTC");
        },
        // TODO: to merge into _toDateTime function
        _fromDateStrToDateTime(
            value,
            toSecond = false,
            convertTz = true,
            setTimeToNow = false
        ) {
            if (isString(value)) {
                let time = DateTime.fromFormat(value, this._dateFormat, {
                    zone: convertTz ? this._timeZone : "UTC"
                });
                if (time.isValid) {
                    if (setTimeToNow) {
                        // Get the current local time
                        const currentTime = DateTime.now().setZone(
                            convertTz ? this._timeZone : "UTC"
                        );

                        // Set the time of parsedDate to the current local time
                        time = time.set({
                            hour: currentTime.hour,
                            minute: currentTime.minute,
                            second: currentTime.second,
                            millisecond: currentTime.millisecond
                        });
                    }
                    if (toSecond) {
                        time = time.toSeconds();
                    }
                    return time;
                } else {
                    return null;
                }
            } else {
                // some of initialize comes in Moment datetime. Thus, convert to second using unix()
                let time = value;
                if (toSecond) {
                    time = time.unix();
                }
                return time;
            }
        },
        _fromDateTime(value = throwErrorParams(), format = throwErrorParams()) {
            if (!value) {
                return null;
            }

            if (!DateTime.isDateTime(value)) {
                throw "value is not DateTime object";
            }

            switch (format.toLowerCase()) {
                case "string":
                    return value.toFormat(value, "hh:mm a");
                case "moment":
                    return moment(value.toISO()).tz(
                        value.zoneName ? value.zoneName : this._timeZone
                    );
                case "date":
                    return value.toJSDate();
                case "datetime":
                    return value;

                default:
                    return value;
            }
        },

        //"the input "2020-02-14 10:51:05.000000 UTC" can't be parsed as format yyyy-MM-dd HH:mm:ss.SSS z"
        _fromDateCarbon(carbon) {
            let date = null;
            switch (carbon.timezone_type) {
                case 1:
                    date = DateTime.fromFormat(
                        carbon.date + " " + carbon.timezone,
                        "yyyy-MM-dd HH:mm:ss.SSS000 ZZ"
                    );
                    break;
                case 2:
                case 3:
                    // TODO: after conversion, seem it convert to timezone +8.00
                    // even setting staff and clinic set to other timezone
                    if (["UTC", "GMT"].includes(carbon.timezone)) {
                        date = DateTime.fromFormat(
                            carbon.date + " +0:00",
                            "yyyy-MM-dd HH:mm:ss.SSS000 ZZ"
                        );
                    } else {
                        date = DateTime.fromFormat(
                            carbon.date + " " + carbon.timezone,
                            "yyyy-MM-dd HH:mm:ss.SSS000 z"
                        );
                    }
                    break;
                default:
                    throw "Undefined timezone_type";
            }

            if (date.invalid) {
                throw date.invalid.reason + "" + date.invalid.explanation;
            }

            return date;
        },
        _carbonDateDisplay(carbon, customFormat = null) {
            if (carbon && _.isObject(carbon)) {
                let date = this._fromDateCarbon(carbon).setZone(this._timeZone);
                if (date.isValid) {
                    return date.toFormat(customFormat || this._dateFormat);
                }
            }

            return "--";
        },
        _carbonDateTimeDisplay(carbon) {
            if (carbon && _.isObject(carbon)) {
                let date = this._fromDateCarbon(carbon).setZone(this._timeZone);
                if (date.isValid) {
                    return (
                        date.toFormat(this._dateFormat) +
                        " " +
                        date.toFormat("hh:mm a")
                    );
                }
            }
            return "--";
        },
        _setDateDisplay(rawDate) {
            if (rawDate && !_.isObject(rawDate)) {
                let date = this._toDateTime(rawDate);
                if (date.isValid) {
                    return date.toFormat(this._dateFormat);
                }
            }
            return "--";
        },
        _setTimeDisplay(rawDate, toFloat = true) {
            if (rawDate && !_.isObject(rawDate)) {
                let date = this._toDateTime(rawDate);
                if (date.isValid) {
                    const hour12 = date.toFormat("h");
                    const meridiem = date.toFormat("a");
                    const minute = date.minute;
                    const minuteString = minute.toString().padStart(2, "0");
                    const time = toFloat
                        ? parseFloat(`${hour12}:${minuteString}`)
                        : `${hour12}:${minuteString}`;
                    return `${time} ${meridiem}`;
                }
            }
            return "--";
        },
        _setDateTimeDisplay(rawDate, convertTz = true) {
            if (rawDate && !_.isObject(rawDate)) {
                let date = this._toDateTime(rawDate, convertTz);
                if (date.isValid) {
                    return (
                        date.toFormat(this._dateFormat) +
                        " " +
                        date.toFormat("hh:mm a")
                    );
                }
            }
            return "--";
        },
        // change formatted date string into clinic selected date format
        _reformatDateDisplay(value, initFormat = "yyyy-MM-dd", newFormat) {
            if (isString(value)) {
                let date = DateTime.fromFormat(value, initFormat);
                let format = newFormat ? newFormat : this._dateFormat;

                if (date.isValid) {
                    return date.toFormat(format);
                } else {
                    return "";
                }
            }

            return "";
        },
        _redirect(url, openWindow, timeout) {
            if (setTimeout) {
                setTimeout(function () {
                    if (openWindow == true) {
                        window.open(url, "_blank");
                    } else {
                        window.location.href = url;
                    }
                }, timeout);
            } else {
                if (openWindow == true) {
                    window.open(url, "_blank");
                } else {
                    window.location.href = url;
                }
            }
        },
        _replaceTimestamp(str) {
            let self = this;
            return str.replace(/{(.*?)}/g, function (match, key) {
                return moment.unix(key).format(self.getDateFormat());
            });
        },
        _isExtensionEnabled(code) {
            let ext = find(this.$root.extensions, {
                extension_code: code
            });
            return ext ? !!ext.id : false;
        },
        _arrayToSentence(arr) {
            if (!isArray(arr) || !arr.length) {
                return "";
            }

            return (
                arr.slice(0, -2).join(", ") +
                (arr.slice(0, -2).length ? ", " : "") +
                arr.slice(-2).join(" and ")
            );
        },
        _parseLocation(loc) {
            if (!loc) {
                return null;
            }
            if (isObject(loc)) {
                return `[${loc.type}]${loc.id}`;
            }
            return loc;
        },
        async _getSetting() {
            if (this.$root.setting && this.$root.setting.id) {
                return this.$root.setting;
            } else {
                return await new Promise(resolve =>
                    this.$root.$once("settingLoaded", resolve)
                );
            }
        },
        async _getSubscription() {
            try {
                let response = await this.$http.get(
                    process.env.APPS + "index.php/ajax/subscription/status"
                );
                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async _getSubscriptionConfig() {
            try {
                let response = await this.$http.get(
                    process.env.APPS + "index.php/business/subscription/config"
                );
                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async _taskCheck() {
            if (this.$route.path.includes("tv/")) {
                return [];
            }
            try {
                let response = await this.$http.get(
                    process.env.APPS + "index.php/schedule/task/task_check"
                );

                if (get(response, "data.tasks_due") == 1) {
                    localStorage.setItem(
                        response.data.storage_key,
                        JSON.stringify({
                            message: response.data.message,
                            is_view: 0
                        })
                    );
                } else {
                    localStorage.setItem(response.data.storage_key, false);
                }

                this.$store.commit(
                    "task/setBadgeCount",
                    response?.data?.tasks_count ?? 0
                );

                this._toggle_bell_notification(
                    response.data.business_id,
                    response.data.user_id
                );
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async _toggle_bell_notification(business_id, user_id) {
            let storageKey = `${business_id}_${user_id}_available_task`;
            let localData = localStorage.getItem(storageKey);
            let notificationData = localData ? JSON.parse(localData) : null;

            function displayNotification() {
                if (
                    notificationData.is_view === 0 &&
                    notificationData.message
                ) {
                    let userPreferences = this.$store.state?.userPreferences
                        ?.prefs?.task;

                    if (userPreferences?.show_banner) {
                        setTimeout(() => {
                            const match = notificationData?.message?.match(
                                /<a[^>]*>(.*?)<\/a>/
                            );

                            const content = match
                                ? match[1]
                                : "New tasks assigned to you"; // fallback message

                            this._message(
                                `<a href="#" onclick="routerNavigate(event, '/schedule/tasks?status=3')">${content}</a>`,
                                "info",
                                null,
                                null,
                                {
                                    isTask: true,
                                    "close-callback": () => {
                                        this.$http.post(
                                            `${process.env.APPS}index.php/schedule/task/close_notification`
                                        );
                                        this._updateIsView(storageKey, 1);
                                    }
                                }
                            );
                        }, 3000);
                    }
                }
            }

            if (notificationData?.message && localData !== "false") {
                displayNotification.call(this);
            }
        },
        _updateIsView: function (key, status) {
            //this function is from TASK.updateIsView
            if (localStorage.getItem(key)) {
                var data = JSON.parse(localStorage.getItem(key));
                // --- update is_view
                localStorage.setItem(
                    key,
                    JSON.stringify({
                        message: data.message,
                        is_view: status
                    })
                );
            }
        },
        uniqid() {
            return uniqid();
        },
        async _saveMedicalNote(data) {
            let self = this;

            let post = {
                id: data.id,
                isShortcut: data.isShortcut,
                isShortcut_pid: data.patient ? data.patient.id : null,
                patient: data.patient ? data.patient.fullname : null,
                cid: data.client ? data.client.id : null,
                entity_id: data.referral_id ? data.referral_id : null,
                entity_type: data.referrer ? data.referrer.type : null,
                entity_name: data.referrer ? data.referrer.label : null,
                start: data.date.format(self.getDateFormat()),
                note_date: data.note_date.format(self.getDateFormat()),
                note_time: data.note_date.format("h:mm A"),
                template: data.soap_id,
                complaint: data.complaint,
                bid: data.staffs ? data.staffs.actor_id : null,
                by: data.staffs ? data.staffs.fullname : null
            };

            const response = await $.post(
                process.env.APPS + "index.php/patient/soap/save",
                post
            );

            // -- print letter
            if (response && response.status == 0) {
                return response;
            }

            return response;
        },
        async _saveEstimate(data, bundle_data) {
            let self = this;

            let post = {
                id: data.id,
                cid: data.client ? data.client.id : null,
                pid: data.patient ? data.patient.id : null,
                status: data.status,
                bid: data.staff ? data.staff.id : null,
                action_status: data.action_status,
                start_time: data.start.format("h:mm A"),
                business_location: this.$root.setting.location,
                isShortcut: data.isShortcut,
                title: data.title,
                client: data.client ? data.client.fullName : null,
                patient: data.patient ? data.patient.name : null,
                location_id: data.location ? data.location.id : null,
                start: data.start.format(self.getDateFormat()),
                expire: data.expire.format(self.getDateFormat()),
                template: data.bundle_id ? data.bundle_id : [],
                note: data.note
            };

            if (
                bundle_data.selectedElements &&
                bundle_data.selectedElements.length > 0
            ) {
                bundle_data.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;
                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }
                });
            }

            const response = await this.$http.post(
                process.env.APPS + "index.php/billing/invoice/save",
                $.param(post)
            );

            return response.data;
        },
        _now() {
            return this.$root.setting.serverTime
                ? DateTime.fromObject({ zone: this._timeZone }).plus(
                      this.$root.setting.serverTime.offset,
                      "millisecond"
                  )
                : DateTime.fromObject({ zone: this._timeZone });
        },
        async _saveInvoice(data, bundle_data) {
            let self = this;

            let post = {
                id: data.id,
                cid: data.client ? data.client.id : null,
                pid: data.patient ? data.patient.id : null,
                status: data.status,
                bid: data.staff ? data.staff.id : null,
                action_status: data.action_status,
                start_time: data.start.format("h:mm A"),
                business_location: this.$root.setting.location,
                isShortcut: data.isShortcut,
                client: data.client ? data.client.fullName : null,
                patient: data.patient ? data.patient.name : null,
                location_id: data.location
                    ? this._parseLocation(data.location)
                    : null,
                start: data.start.format(self.getDateFormat()),
                expire: data.expire.format(self.getDateFormat()),
                template: data.bundle_id,
                soap_id: data.soap,
                note: data.note
            };

            if (
                bundle_data.selectedElements &&
                bundle_data.selectedElements.length > 0
            ) {
                bundle_data.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;
                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }

                    if (item.tag_number) {
                        post["fm_bundle_element_tag_number_" + item.id] =
                            item.tag_number;
                    }
                });
            }

            const response = await this.$http.post(
                process.env.APPS + "index.php/billing/invoice/save",
                $.param(post)
            );

            return response.data;
        },
        _saveAudit(params) {
            let modules = params.path.split("/");
            this.$http.post(process.env.BARRACUDA + "account/audit", {
                module: modules[1],
                sub_module: modules[2],
                action: params.action
                    ? params.action
                    : modules[3]
                    ? modules[3]
                    : "index",
                reference_id: params.reference_id || "",
                keys:
                    this.$root.currentActor.id +
                    "." +
                    this.$store.state.setting.business_id +
                    "." +
                    modules[2] +
                    "." +
                    modules[1] +
                    "." +
                    (modules.length > 3 ? modules[3] : "index"),
                url: "../view" + params.fullPath,
                is_ajax: 0
            });
        },
        _isTouchDevice() {
            return (
                "ontouchstart" in window || "onmsgesturechange" in window // works on most browsers
            ); // works on IE10 with some false positives
        },
        _getStatusOptionArray(statusAction) {
            if (statusAction === this.STATUS_ACTION.WRITEOFF) {
                return [
                    {
                        value: this.STATUS_ACTION_OPTION.WRITEOFF_BALANCE,
                        label: "Write off balance"
                    },
                    {
                        value: this.STATUS_ACTION_OPTION.DO_NOTHING,
                        label: "Keep outstanding balance"
                    }
                ];
            } else if (statusAction === this.STATUS_ACTION.REFUND) {
                return [
                    {
                        value: this.STATUS_ACTION_OPTION.REFUND_BALANCE,
                        label: "Convert to refund"
                    },
                    {
                        value: this.STATUS_ACTION_OPTION.DO_NOTHING,
                        label: "Keep credit balance"
                    }
                ];
            }

            return [];
        },
        _priceFormatter(value, symbol) {
            let price;
            if (Number.isInteger(value)) {
                price = value;
            } else {
                price = value * 1;
            }
            return (
                symbol + price.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,")
            );
        },
        /**
         * Format phone number using country code and phone number
         * @param value - String
         * @param callingCode - Number, String
         * @param format - default "E.164", Available formats:
                NATIONAL — Example: "(213) 373-4253"
                INTERNATIONAL — Example: "+1 213 373 4253"
                E.164 — Example: "+12133734253"
                RFC3966 (the phone number URI) — Example: "tel:+12133734253;ext=123"
                IDD
         * @returns String, formatted phonenumber
         *  */
        _formatPhone(value, callingCode = null, format = "E.164") {
            if (!value) {
                return "";
            }
            // eslint-disable-next-line no-undef
            let phoneNumber = new libphonenumber.parsePhoneNumber(value, {
                defaultCallingCode: callingCode
                    ? callingCode
                    : this.$store.state.setting.phone_code
            });

            return phoneNumber.format(format);
        },
        valid_uuid(val) {
            var valid = false;
            if (val != null && val.length > 0) {
                var val2 = val.replace(/0/g, "");
                if (val2.trim() !== "") {
                    valid = true;
                }
            }
            return valid;
        },
        capitalize(words) {
            return words.replace(/(^\w{1})|(\s+\w{1})/g, letter =>
                letter.toUpperCase()
            );
        },
        dynamicPageTitle(title) {
            // Dynamically change the browser page title
            window.document.title = entities.decodeHTML(this.capitalize(title));
        },
        convertNewLineToBreak(text) {
            if (text == "null" || text === null) {
                return "";
            } else {
                return text ? text.replace(/\n/g, "<br />") : text;
            }
        },
        roundOff(number) {
            return Math.round(Number(number) * 100) / 100;
        },
        checkNullCharsUnicode(value) {
            // eslint-disable-next-line no-control-regex
            let regex = /\u0000+/;
            return regex.test(value);
        },
        isOwner(businessId) {
            if (businessId == this._setting.business_id) {
                return true;
            }
            return false;
        },
        _sleep(milliseconds) {
            return new Promise(r => setTimeout(r, milliseconds));
        },
        decodeHtmlEntities(str) {
            // The regex checks for patterns like &name; and &#code;
            const entityPattern = /&([a-zA-Z]+|#[0-9]+);/;
            if (entityPattern.test(str)) {
                const textarea = document.createElement("textarea");
                textarea.innerHTML = str;
                return textarea.value;
            }
            return str;
        },
        async _activeSoapInvoice(encodedId) {
            if (!encodedId) {
                return;
            }
            let data = await this.$store.dispatch(
                "soap/getSoapDetails",
                encodedId
            );

            if (data.message.code == 200) {
                // get invoices tied to soap
                let invoices = data?.resource?.invoices;
                // then sort
                const sortedInvoices = [...invoices].sort((a, b) => {
                    return (
                        new Date(b.update_at.date) - new Date(a.update_at.date)
                    );
                });
                // then find first invoices
                const result = sortedInvoices?.find(
                    invoice =>
                        invoice.is_invoice == 1 &&
                        invoice.isactive == 1 &&
                        invoice.status == 1
                );

                return result || null;
            }
        },
        _sanitizeAndTitleCaseString(str) {
            return str
                .split("_")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
        handleActionResponse(response, messageCallback) {
            if (!response || !response.action_response) return;

            const actionResponses = Array.isArray(response.action_response)
                ? response.action_response
                : Object.values(response.action_response);

            actionResponses.forEach(actionResponse => {
                if (typeof actionResponse === "object") {
                    Object.entries(actionResponse).forEach(([key, value]) => {
                        if (key === "uri") window.open(value, "_blank");
                        if (key === "success") messageCallback(value);
                        if (key === "error") messageCallback(value, "danger");
                    });
                }
            });
        },
        _isEnhancedLayoutMobile() {
            const maxWidth = 500;

            if (
                window.matchMedia(`(max-width: ${maxWidth}px)`).matches &&
                this._isEnhancedLayout
            ) {
                return true;
            }

            return false;
        },
        _toggleEnhancedLayoutMobileSideMenu(toggle = null) {
            if (!this._isEnhancedLayoutMobile()) {
                return;
            }

            this.$store.commit("toggleSideMenu", toggle);
        }
    }
});
